import { slugify, toSlug } from '$lib/utils/string'

import pokemon from './fr/pokemon.json'
import abilities from './fr/abilities.json'
import moves from './fr/moves.json'
import items from './fr/items.json'
import locations from './fr/locations.json'
import trainers from './fr/trainers.json'
import types from './fr/types.json'
import uiStrings from './ui.json'

const FR = { pokemon, abilities, moves, items, locations, trainers, types }

const lookup = (name, pool) => {
  if (!name) return null
  const exact = pool[name.toLowerCase()] || pool[name]
  if (exact) return exact

  // PokeAPI-style slugs use hyphens, but display names often use spaces,
  // e.g. "Karate Chop" -> "karate-chop". Also strip punctuation such as
  // apostrophes for moves like "King's Shield".
  const slugged = pool[slugify(name)] || pool[toSlug(name)]
  if (slugged) return slugged

  if (/ \(.*?\)$/.test(name)) {
    const base = name.replace(/ \(.*?\)$/, '')
    return (
      pool[base.toLowerCase()] ||
      pool[base] ||
      pool[slugify(base)] ||
      pool[toSlug(base)] ||
      null
    )
  }
  return null
}

/**
 * Translate a single name (pokemon, location, trainer, ability, move,
 * item) to the target locale. Falls back to the original name when no
 * translation exists.
 */
export const t = (name, lang = 'en') => {
  if (lang !== 'fr' || !name || typeof name !== 'string') return name

  const region = name.match(/^(Galarian|Alolan|Hisuian|Paldean) (.+)$/)
  if (region) {
    const base = lookup(region[2], FR.pokemon)
    if (base) {
      const prefix = {
        Galarian: 'de Galar',
        Alolan: "d'Alola",
        Hisuian: 'de Hisui',
        Paldean: 'de Paldea'
      }[region[1]]
      return `${base} ${prefix}`
    }
  }

  const mega = name.match(/^Mega (.+)$/)
  if (mega) {
    const base = lookup(mega[1], FR.pokemon)
    if (base) return `Méga-${base}`
  }

  if (/^Route \d+$/.test(name)) return name
  if (/^Routes \d+$/.test(name)) return name.replace(/^Routes/, 'Route')

  for (const key of ['types', 'locations', 'trainers', 'pokemon', 'abilities', 'moves', 'items']) {
    const res = lookup(name, FR[key])
    if (res) return res
  }
  return name
}

/**
 * Slugified translation, handy for search matching.
 */
export const tSlug = (name, lang) =>
  t(name, lang)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

/**
 * Translate a UI string key to the target locale.
 * Supports simple `{key}` interpolation via the `vars` object.
 */
export const ui = (key, lang = 'en', vars = {}) => {
  const dict = uiStrings[lang] || uiStrings.en
  let text = dict?.[key] ?? uiStrings.en[key] ?? key
  if (vars && typeof text === 'string') {
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v)
    })
  }
  return text
}

/**
 * Translate Nuzlocke status names.
 */
const statusKeys = {
  Captured: 'captured',
  Received: 'received',
  Shiny: 'shiny',
  Traded: 'traded',
  Missed: 'missed',
  Dead: 'dead',
  Trash: 'trash'
}

export const tState = (state, lang = 'en') => {
  const key = statusKeys[state]
  if (!key) return state
  return ui(key, lang)
}
